import { useEffect, useRef, useState, forwardRef, InputHTMLAttributes } from 'react';
import { Search } from 'lucide-react';
import { Input } from './Input';

export interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  /** Controlled value. Jeśli chcesz onDebouncedChange, zarządzaj tym z zewnątrz. */
  value?: string;
  onChange?: (value: string) => void;
  /** Wywoływane z debounce (default 300ms). */
  onDebouncedChange?: (value: string) => void;
  debounceMs?: number;
}

/**
 * Input dedykowany do wyszukiwania. Ikona Search po lewej, clearable X po
 * prawej. Opcjonalny debounce — wygodny dla API search calls bez odpalania
 * request na każdy keystroke.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { value: controlledValue, onChange, onDebouncedChange, debounceMs = 300, placeholder = 'Szukaj…', ...rest },
  ref,
) {
  const [internal, setInternal] = useState('');
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internal;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const setValue = (next: string) => {
    if (!isControlled) setInternal(next);
    onChange?.(next);
    if (onDebouncedChange) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => onDebouncedChange(next), debounceMs);
    }
  };

  return (
    <Input
      ref={ref}
      type="search"
      leftIcon={<Search />}
      clearable
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onClear={() => setValue('')}
      placeholder={placeholder}
      {...rest}
    />
  );
});
